'use client'

import { useEffect, useRef, useState } from 'react'

import Button from 'shared/ui/Button'
import TextField from 'shared/ui/form/TextField'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { getVerificationSms } from '../../api/auth'

import styles from './ModalSmsConfirmation.module.scss'

interface ModalSmsConfirmationProps {
  onConfirm: (code: string) => void
  phoneNumber: string
  onClose: () => void
}

type OtpRequest = CredentialRequestOptions & { otp: { transport: string[] } }

const ModalSmsConfirmation: React.FC<ModalSmsConfirmationProps> = ({
  onConfirm,
  phoneNumber,
  onClose
}) => {
  const [code, setCode] = useState('')
  const [resendCountdown, setResendCountdown] = useState(15)
  const [validityCountdown, setValidityCountdown] = useState(180)
  const [otpRequestId, setOtpRequestId] = useState(0)

  const resendIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const validityIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current)
      if (validityIntervalRef.current)
        clearInterval(validityIntervalRef.current)
    }
  }, [])

  const startResendCountdown = () => {
    if (resendIntervalRef.current) clearInterval(resendIntervalRef.current)
    setResendCountdown(15)
    resendIntervalRef.current = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current!)

          return 0
        }

        return prev - 1
      })
    }, 1000)
  }

  const startValidityCountdown = () => {
    if (validityIntervalRef.current) clearInterval(validityIntervalRef.current)
    setValidityCountdown(180)
    validityIntervalRef.current = setInterval(() => {
      setValidityCountdown(prev => {
        if (prev <= 1) {
          clearInterval(validityIntervalRef.current!)

          return 0
        }

        return prev - 1
      })
    }, 1000)
  }

  // WebOTP (Android Chrome): reads the code from an SMS ending with `@domain #code`
  useEffect(() => {
    if (!('OTPCredential' in window)) return

    const abortController = new AbortController()
    const request: OtpRequest = {
      otp: { transport: ['sms'] },
      signal: abortController.signal
    }

    navigator.credentials
      .get(request)
      .then(credential => {
        if (credential && 'code' in credential) {
          setCode(String(credential.code))
        }
      })
      .catch(() => undefined)

    return () => abortController.abort()
  }, [otpRequestId])

  const handleResendCode = async () => {
    try {
      setCode('')
      startResendCountdown()
      startValidityCountdown()
      setOtpRequestId(prev => prev + 1)
      await getVerificationSms(phoneNumber)

      return true
    } catch (err) {
      toast.error(getApiErrors(err))
      console.error(err)

      return false
    }
  }

  useEffect(() => {
    startResendCountdown()
    startValidityCountdown()
  }, [])

  const handleClose = () => {
    onClose()
    setCode('')
  }

  const handleCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCode(e.target.value)
  }

  const handleConfirm = () => {
    onConfirm(code)
  }

  return (
    <div className={styles.wrapper}>
      <Modal id='sms-confiramation-modal' open={true} onClose={handleClose}>
        <ModalTitle>Weryfikacja numeru </ModalTitle>
        {validityCountdown > 0 ? (
          <div className={styles.modalContent}>
            Podaj kod SMS otrzymany na numer telefonu wskazany w formularzu
            rejestracji. Kod ważny będzie przez:
            <div className={styles.counterDisplay}>{validityCountdown}s</div>
          </div>
        ) : (
          <div className={styles.expiredCode}>Kod wygasł, proszę wygenerować nowy.</div>
        )}
        <div className={styles.codeHandlingWrapper}>
          <TextField
            marginBottom={false}
            label='Kod z wiadomości SMS'
            id='sms-code'
            name='smsCode'
            inputProps={{ autoComplete: 'one-time-code', inputMode: 'numeric' }}
            onChange={handleCodeChange}
            value={code}
          />
          {resendCountdown > 0 ? (
            <div className={styles.resendCountdownPending}>
              Wyślij kod ponownie {resendCountdown && `(${resendCountdown})`}s
            </div>
          ) : (
            <div
              className={styles.resendCountdownResolved}
              onClick={handleResendCode}
              role='button'
              tabIndex={0}
            >
              Wyślij kod ponownie
            </div>
          )}
        </div>
        <div className={styles.modalActions}>
          <Button
            onClick={handleConfirm}
            disabled={code.length !== 6}
            variant='primary'
            size='large'
            type='button'
          >
            Potwierdzam
          </Button>
          <Button onClick={handleClose} variant='primaryOutline' size='large'>
            Anuluj
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default ModalSmsConfirmation
